import { useEffect, useState } from 'react';
import { collapsedForFocus, toggleCollapsed } from '@evogard/ui';
import { useSavePreferences, useSessionContext } from './session.js';

/**
 * As colunas recolhidas de um quadro por fase (28/10/2026) — o par que o
 * `Kanban` recebe em `collapsed` e `onCollapse`.
 *
 * Duas fontes, e só uma vale por vez:
 *
 * - **o que a pessoa recolheu**, gravado nas preferências dela
 *   (`preferences.collapsedColumns[board]`), que vale na próxima visita e em
 *   outro computador. As colunas nascem abertas;
 * - **o filtro de fase**: escolhida uma, a coluna dela fica aberta e as outras
 *   viram faixa, em vez de sumir. É da visita: abrir uma faixa com o filtro
 *   ligado não grava nada, e limpar o filtro devolve o quadro da pessoa.
 *
 * Como na Mesa da Cobrança, a lista da tela vale a partir do primeiro clique e
 * vai ao servidor sem esperar a resposta. O `?.` em `collapsedColumns` é pela
 * API de uma versão antes, que não tinha o campo (`DeskBoardScreen.tsx`).
 */
export function useCollapsedStages(
  board: string,
  stageIds: readonly string[],
  focus: string | undefined,
) {
  const { data: context } = useSessionContext();
  const savePreferences = useSavePreferences();
  const [touched, setTouched] = useState<string[] | null>(null);
  const saved = touched ?? context?.preferences.collapsedColumns?.[board] ?? [];

  /* O que a pessoa abriu ou recolheu com o filtro ligado — some quando ele muda. */
  const [visit, setVisit] = useState<string[] | null>(null);
  useEffect(() => setVisit(null), [focus]);
  const filtered = focus !== undefined && stageIds.includes(focus);

  const collapsed = filtered
    ? (visit ?? collapsedForFocus(stageIds, focus))
    : saved.filter((id) => stageIds.includes(id));

  const onCollapse = (stageId: string, value: boolean) => {
    if (filtered) {
      setVisit(toggleCollapsed(collapsed, stageId, value));
      return;
    }
    const next = toggleCollapsed(saved, stageId, value);
    setTouched(next);
    savePreferences.mutate({ collapsedColumns: { [board]: next } });
  };

  return { collapsed, onCollapse };
}
